'use client';

import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import clsx from 'clsx';
import { ThirdPartSelectors } from '@headless-workspace/config';
import { GlowButtonProps, GlowIconProps, PropsWithClassName } from '@headless-workspace/glow-ds';
import { Node } from '@headless-workspace/typings';
import { EmblaOptionsType } from 'embla-carousel';
import { UICarouselNavigationButton } from '../UICarouselNavigationButton';
import { CarouselLogic } from './useCarouselLogic';

type UIFullScreenMobileCarouselProps<T> = PropsWithClassName &
    CarouselLogic & {
        items: T[];
        renderItem: (item: T, index: number) => Node;
        options?: EmblaOptionsType;
        prevBtnAriaLabel: string;
        nextBtnAriaLabel: string;
        thumbArialLabel: string;
        navigationButtonClassName?: string;
        showDot?: boolean;
        navButtonIconType?: GlowIconProps['type'];
        navButtonVariant?: GlowButtonProps['variant'];
        navButtonSize?: GlowButtonProps['size'];
        displayDisabledButtons?: boolean;
        ariaLabel?: string;
    };

export const UIFullScreenMobileCarousel = <T,>({
    className,
    emblaRef,
    nextBtnDisabled,
    prevBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
    selectedIndex,
    items,
    renderItem,
    prevBtnAriaLabel,
    nextBtnAriaLabel,
    navigationButtonClassName,
    showDot = true,
    navButtonIconType = 'medium',
    navButtonVariant = 'primary',
    navButtonSize = 'medium',
    displayDisabledButtons = false,
    ariaLabel,
}: UIFullScreenMobileCarouselProps<T>): ReactNode => {
    const t = useTranslations('Discover.Pdp.carousel');
    const carouselAriaLabel = ariaLabel ?? t('ariaLabel.carousel');

    const displayPrevButton = !prevBtnDisabled || displayDisabledButtons;
    const displayNextButton = !nextBtnDisabled || displayDisabledButtons;

    return (
        <section
            className={clsx('flex flex-row items-center justify-center gap-0 mx-auto', className)}
            aria-label={carouselAriaLabel}
            id={ThirdPartSelectors.ProductMediaCarousel}
        >
            {displayPrevButton && (
                <UICarouselNavigationButton
                    type={'prev'}
                    onClick={onPrevButtonClick}
                    disabled={prevBtnDisabled}
                    className={navigationButtonClassName}
                    ariaLabel={prevBtnAriaLabel}
                    iconType={navButtonIconType}
                    variant={navButtonVariant}
                    size={navButtonSize}
                />
            )}
            <div className={'flex flex-col gap-1 overflow-hidden'}>
                <div
                    className={'w-full m-auto overflow-hidden'}
                    ref={emblaRef}
                    role={'group'}
                    aria-roledescription={carouselAriaLabel}
                >
                    <ul className={'flex flex-row gap-0.5 touch-pan-y touch-pinch-zoom max-h-seventyViewport'}>
                        {items.map((item, index) => {
                            return (
                                <li className={clsx(!nextBtnDisabled && 'last:mr-0.5')} key={`carousel-item-${index}`}>
                                    {renderItem(item, index)}
                                </li>
                            );
                        })}
                    </ul>
                </div>
                {showDot && (
                    <div className={'flex justify-center gap-0.25 p-0.25'}>
                        {items.map((_, index) => (
                            <span
                                key={`dots-item-${index}`}
                                className={clsx(
                                    'w-[0.25rem] h-[0.25rem] m-0.125 bg-text-tertiary rounded-1',
                                    'ease-in-out transition-transform duration-moderate3',
                                    index === selectedIndex && '!w-animatedIconDotPdp',
                                )}
                            />
                        ))}
                    </div>
                )}
            </div>
            {displayNextButton && (
                <UICarouselNavigationButton
                    type={'next'}
                    onClick={onNextButtonClick}
                    disabled={nextBtnDisabled}
                    className={navigationButtonClassName}
                    ariaLabel={nextBtnAriaLabel}
                    iconType={navButtonIconType}
                    variant={navButtonVariant}
                    size={navButtonSize}
                />
            )}
        </section>
    );
};
